import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  useTheme,
  Collapse,
  styled,
  alpha,
  StepIconProps,
  useMediaQuery,
  SvgIconProps,
  Fade,
  Tooltip,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { getStepIconColor, getStepIconTextColor, getProgressBarWidth } from '../utils';
import { StepperProps, StepType } from '../types';
import { useTranslation } from 'react-i18next';

const ColorlibStepIconRoot = styled('div')<{
  ownerState: { completed?: boolean; active?: boolean };
}>(({ theme, ownerState }) => ({
  backgroundColor: getStepIconColor(
    theme,
    ownerState.active || false,
    ownerState.completed || false,
  ),
  zIndex: 1,
  color: getStepIconTextColor(
    theme,
    ownerState.active || false,
    ownerState.completed || false,
  ),
  width: 45,
  height: 45,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  transition: theme.transitions.create(['background-color', 'box-shadow', 'transform'], {
    duration: theme.transitions.duration.short,
  }),
  boxShadow: ownerState.active
    ? `0 0 0 4px ${alpha(theme.palette.primary.main, 0.12)}`
    : 'none',
  transform: ownerState.active ? 'scale(1.1)' : 'scale(1)',
  '& .MuiSvgIcon-root': {
    fontSize: 24,
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.short,
    }),
  },
  '&:hover': {
    transform: 'scale(1.05)',
    '& .MuiSvgIcon-root': {
      transform: 'scale(1.1)',
    },
  },
}));

const ColorlibStepIcon = (props: StepIconProps) => {
  const { active, completed, className, icon } = props;
  const { t } = useTranslation();

  const renderIcon = useCallback(() => {
    if (!icon && icon !== 0) {
      return null;
    }

    if (completed) {
      return (
        <CheckIcon
          fontSize="small"
          data-testid="step-icon-check"
        />
      );
    }

    if (typeof icon === 'number') {
      return <span data-testid={`step-icon-number-${icon}`}>{icon}</span>;
    }

    if (React.isValidElement<SvgIconProps>(icon)) {
      return React.cloneElement(icon, { fontSize: 'small' });
    }

    return null;
  }, [icon, completed]);

  const status = completed ? 'completed' : active ? 'active' : 'inactive';
  const statusLabel = t(`wizard.stepper.status.${status}`, {
    defaultValue: status,
  });
  const iconAriaLabel = t('wizard.stepper.iconLabel', {
    status: statusLabel,
    defaultValue: 'Step {{status}}',
  });

  return (
    <ColorlibStepIconRoot
      ownerState={{ completed, active }}
      className={className}
      data-testid={`step-icon-${status}`}
      role="img"
      aria-label={iconAriaLabel}
    >
      {renderIcon()}
    </ColorlibStepIconRoot>
  );
};

const CompactStepper = ({
  steps,
  activeStep,
  onStepClick,
}: {
  steps: StepperProps['steps'];
  activeStep: number;
  onStepClick?: (step: number) => void;
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const normalizedSteps = useMemo<StepType[]>(
    () =>
      steps.map((step, index) => ({
        label: step.label,
        icon: step.icon ?? index + 1,
        completed: step.completed,
        active: step.active,
        disabled: step.disabled,
      })),
    [steps],
  );

  const totalSteps = Math.max(normalizedSteps.length, 1);
  const currentStep = normalizedSteps[Math.min(activeStep, totalSteps - 1)] ?? {
    label: '',
    icon: activeStep + 1,
  };

  const toggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const handleStepSelection = useCallback(
    (index: number) => {
      if (index <= activeStep && onStepClick) {
        onStepClick(index);
        setIsExpanded(false);
      }
    },
    [activeStep, onStepClick],
  );

  const progressPercentage = useMemo(
    () => getProgressBarWidth(activeStep, totalSteps),
    [activeStep, totalSteps],
  );

  const currentStepNumber = Math.min(activeStep + 1, totalSteps);
  const stepCounterLabel = t('wizard.stepper.stepCounter', {
    current: currentStepNumber,
    total: totalSteps,
    defaultValue: 'Step {{current}} of {{total}}',
  });
  const listLabel = t('wizard.stepper.listLabel', {
    defaultValue: 'All steps',
  });

  return (
    <Box
      sx={{ width: '100%', position: 'relative' }}
      data-testid="compact-stepper"
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: 4,
          backgroundColor: theme.palette.grey[300],
          borderRadius: '4px 4px 0 0',
        }}
        role="progressbar"
        aria-valuenow={Math.min(activeStep + 1, totalSteps)}
        aria-valuemin={1}
        aria-valuemax={totalSteps}
        data-testid="stepper-progress-bar"
      >
        <Box
          sx={{
            width: progressPercentage,
            height: '100%',
            backgroundColor: theme.palette.primary.main,
            transition: theme.transitions.create('width', {
              duration: theme.transitions.duration.standard,
            }),
            borderRadius: '4px 0 0 0',
          }}
          data-testid="stepper-progress-fill"
        />
      </Box>

      <Box
        sx={{
          backgroundColor: alpha(theme.palette.background.paper, 0.9),
          backdropFilter: 'blur(8px)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            py: 1.5,
            px: 2,
            cursor: 'pointer',
            transition: theme.transitions.create('background-color'),
            '&:hover': {
              backgroundColor: alpha(theme.palette.action.hover, 0.5),
            },
            '&:focus': {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: -2,
            },
          }}
          onClick={toggleExpand}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleExpand();
            }
          }}
          role="button"
          tabIndex={0}
          aria-expanded={isExpanded}
          aria-controls="step-list"
          aria-label={t('wizard.stepper.currentStepToggle', {
            defaultValue: 'Current step: {{label}}. Click to {{action}} all steps',
            label: currentStep.label,
            action: isExpanded
              ? t('wizard.stepper.collapse', { defaultValue: 'collapse' })
              : t('wizard.stepper.expand', { defaultValue: 'expand' }),
          })}
          data-testid="compact-stepper-toggle"
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ColorlibStepIcon
              active
              completed={false}
              icon={currentStep.icon}
            />
            <Box sx={{ ml: 2 }}>
              <Typography
                variant="body2"
                fontWeight="600"
                data-testid="current-step-label"
              >
                {currentStep.label}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                data-testid="current-step-info"
              >
                {stepCounterLabel}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              transition: theme.transitions.create('transform'),
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Box>
        </Box>

        <Collapse
          in={isExpanded}
          timeout={300}
        >
          <Fade
            in={isExpanded}
            timeout={400}
          >
            <Box
              sx={{
                mt: 1,
                pb: 1,
                maxHeight: '60vh',
                overflowY: 'auto',
                overscrollBehavior: 'contain',
              }}
              id="step-list"
              role="list"
              aria-label={listLabel}
              data-testid="step-list"
            >
              {normalizedSteps.map((step, index) => {
                const isActive = index === activeStep;
                const isCompleted =
                  typeof step.completed === 'boolean'
                    ? step.completed
                    : index < activeStep;
                const isDisabled = Boolean(step.disabled);
                const isClickable = !isDisabled && index <= activeStep && !!onStepClick;
                const statusLabel = isDisabled
                  ? t('wizard.stepper.status.disabled', { defaultValue: 'disabled' })
                  : isCompleted
                    ? t('wizard.stepper.status.completed', { defaultValue: 'completed' })
                    : isActive
                      ? t('wizard.stepper.status.active', {
                          defaultValue: 'current step',
                        })
                      : '';
                const statusSuffix = statusLabel
                  ? t('wizard.stepper.statusSuffix', {
                      status: statusLabel,
                      defaultValue: ', {{status}}',
                    })
                  : '';
                const itemBaseLabel = t('wizard.stepper.itemLabel', {
                  label: step.label,
                  current: index + 1,
                  total: totalSteps,
                  defaultValue: '{{label}}, step {{current}} of {{total}}',
                });
                const ariaLabel = `${itemBaseLabel}${statusSuffix}`;

                return (
                  <Box
                    key={`${step.label}-${index}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      py: 1.5,
                      px: 2,
                      opacity: isDisabled ? 0.4 : isActive ? 1 : isCompleted ? 0.8 : 0.6,
                      cursor: isClickable ? 'pointer' : 'default',
                      transition: theme.transitions.create([
                        'background-color',
                        'transform',
                      ]),
                      transform: isActive ? 'translateX(4px)' : 'translateX(0)',
                      '&:hover': {
                        backgroundColor: isClickable
                          ? alpha(theme.palette.primary.main, 0.08)
                          : 'transparent',
                        transform: isClickable ? 'translateX(8px)' : 'translateX(0)',
                      },
                      '&:focus': {
                        outline: `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: -2,
                      },
                    }}
                    onClick={() => {
                      if (isClickable) {
                        handleStepSelection(index);
                      }
                    }}
                    onKeyDown={(e) => {
                      if ((e.key === 'Enter' || e.key === ' ') && isClickable) {
                        e.preventDefault();
                        handleStepSelection(index);
                      }
                    }}
                    role="listitem"
                    tabIndex={isClickable ? 0 : -1}
                    aria-current={isActive ? 'step' : undefined}
                    aria-label={ariaLabel}
                    aria-disabled={isDisabled}
                    data-testid={`compact-step-${index}`}
                  >
                    <ColorlibStepIcon
                      active={isActive}
                      completed={isCompleted}
                      icon={isCompleted ? <CheckIcon /> : step.icon}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        ml: 2,
                        color: isActive
                          ? theme.palette.primary.main
                          : theme.palette.text.secondary,
                        fontWeight: isActive ? 'bold' : 'normal',
                        transition: theme.transitions.create('color'),
                      }}
                      data-testid={`compact-step-label-${index}`}
                    >
                      {step.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Fade>
        </Collapse>
      </Box>
    </Box>
  );
};

const RegularStepper = ({ steps, activeStep, onStepClick }: StepperProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const handleStepClick = useCallback(
    (index: number) => {
      if (index <= activeStep && onStepClick) {
        onStepClick(index);
      }
    },
    [activeStep, onStepClick],
  );

  return (
    <Stepper
      activeStep={activeStep}
      alternativeLabel
      sx={{
        mb: 3,
        '& .MuiStepConnector-line': {
          height: 3,
          border: 0,
          backgroundColor:
            theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#eaeaf0',
          borderRadius: 1,
          transition: theme.transitions.create('background-color'),
        },
        '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': {
          backgroundColor: theme.palette.primary.main,
        },
        '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
          backgroundColor: theme.palette.primary.main,
        },
        '& .MuiStepConnector-root': {
          top: 30,
          left: 'calc(-50% + 25px)',
          right: 'calc(50% + 25px)',
        },
        '& .MuiStepIcon-root': {
          zIndex: 1,
        },
      }}
      data-testid="regular-stepper"
      role="navigation"
      aria-label={t('wizard.stepper.navigationLabel', {
        defaultValue: 'Progress steps',
      })}
    >
      {steps.map(({ label, icon, completed, disabled }, index) => {
        const isActive = index === activeStep;
        const isCompleted =
          typeof completed === 'boolean' ? completed : index < activeStep;
        const isDisabled = Boolean(disabled);
        const isClickable = !isDisabled && index <= activeStep && !!onStepClick;
        const itemBaseLabel = t('wizard.stepper.itemLabel', {
          label,
          current: index + 1,
          total: steps.length,
          defaultValue: '{{label}}, step {{current}} of {{total}}',
        });
        const statusLabel = isDisabled
          ? t('wizard.stepper.status.disabled', { defaultValue: 'disabled' })
          : isCompleted
            ? t('wizard.stepper.status.completed', { defaultValue: 'completed' })
            : isActive
              ? t('wizard.stepper.status.active', { defaultValue: 'current step' })
              : '';
        const statusSuffix = statusLabel
          ? t('wizard.stepper.statusSuffix', {
              status: statusLabel,
              defaultValue: ', {{status}}',
            })
          : '';
        const ariaLabel = `${itemBaseLabel}${statusSuffix}`;
        const tooltipTitle = isClickable
          ? t('wizard.stepper.clickToGo', {
              defaultValue: 'Click to go to this step',
            })
          : '';

        return (
          <Step
            key={`${label}-${index}`}
            disabled={isDisabled}
            completed={isCompleted}
            data-testid={`regular-step-${index}`}
          >
            <Tooltip
              title={tooltipTitle}
              arrow
              disableHoverListener={!isClickable}
            >
              <StepLabel
                onClick={() => {
                  if (isClickable) {
                    handleStepClick(index);
                  }
                }}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && isClickable) {
                    e.preventDefault();
                    handleStepClick(index);
                  }
                }}
                sx={{
                  cursor: isClickable ? 'pointer' : 'default',
                  transition: theme.transitions.create(
                    ['background-color', 'padding', 'transform'],
                    {
                      duration: theme.transitions.duration.shortest,
                    },
                  ),
                  borderRadius: 2,
                  padding: 1,
                  '&:hover': {
                    backgroundColor: isClickable
                      ? alpha(theme.palette.primary.main, 0.08)
                      : 'transparent',
                    transform: isClickable ? 'translateY(-2px)' : 'none',
                  },
                  '&:focus': {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: 2,
                  },
                  '& .MuiSvgIcon-root': {
                    color: isDisabled ? theme.palette.grey[400] : 'inherit',
                  },
                }}
                tabIndex={isClickable ? 0 : -1}
                role={isClickable ? 'button' : undefined}
                aria-label={ariaLabel}
                aria-disabled={isDisabled}
                data-testid={`regular-step-label-${index}`}
                slots={{
                  stepIcon: ColorlibStepIcon,
                }}
                slotProps={{
                  stepIcon: {
                    icon,
                  },
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={isActive ? 'bold' : 'normal'}
                  color={
                    isDisabled ? 'text.disabled' : isActive ? 'primary' : 'text.primary'
                  }
                  sx={{
                    transition: theme.transitions.create('color'),
                  }}
                  data-testid={`regular-step-text-${index}`}
                >
                  {label}
                </Typography>
              </StepLabel>
            </Tooltip>
          </Step>
        );
      })}
    </Stepper>
  );
};

export const AdaptiveStepper = (props: StepperProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();

  return (
    <Box
      data-testid="adaptive-stepper"
      role="region"
      aria-label={t('wizard.stepper.regionLabel', {
        defaultValue: 'Step progress indicator',
      })}
    >
      {isMobile ? <CompactStepper {...props} /> : <RegularStepper {...props} />}
    </Box>
  );
};

export { CompactStepper, RegularStepper, ColorlibStepIcon };
